/**
 * Утилиты для работы с диапазонами дат в статистике
 */

import { endOfDay, endOfMonth, startOfDay, startOfMonth, startOfWeek, subDays, subMonths } from 'date-fns';

import type { DatePresetId, DateRangeOption, ResolvedDateRange } from '../types/account-stats';

const MONTHS_RU_SHORT = [
  'янв.',
  'фев.',
  'мар.',
  'апр.',
  'май',
  'июн.',
  'июл.',
  'авг.',
  'сен.',
  'окт.',
  'ноя.',
  'дек.',
];

const MONTHS_RU_FULL = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

/**
 * Instagram API группирует данные по дням в Pacific Time.
 * PST (ноябрь–март) = UTC-8, полночь = 08:00 UTC
 * PDT (март–ноябрь) = UTC-7, полночь = 07:00 UTC
 */

/** Возвращает UTC-смещение Pacific Time для даты: 8 (PST) или 7 (PDT) */
function pacificMidnightUtcHour(d: Date): number {
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-based

  if (month > 2 && month < 10) return 7;
  if (month < 2 || month > 10) return 8;

  if (month === 2) {
    const firstDay = new Date(year, 2, 1).getDay();
    const secondSunday = firstDay === 0 ? 8 : 15 - firstDay;
    return d.getDate() >= secondSunday ? 7 : 8;
  }

  const firstDay = new Date(year, 10, 1).getDay();
  const firstSunday = firstDay === 0 ? 1 : 8 - firstDay;
  return d.getDate() >= firstSunday ? 8 : 7;
}

export function toInstagramSince(d: Date): number {
  const h = pacificMidnightUtcHour(d);
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), h) / 1000);
}

export function toInstagramUntil(d: Date): number {
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59) / 1000);
}

function formatDateRangeLabel(from: Date, to: Date): string {
  const fromYear = from.getFullYear();
  const toYear = to.getFullYear();
  const fromMonth = MONTHS_RU_SHORT[from.getMonth()];
  const toMonth = MONTHS_RU_SHORT[to.getMonth()];

  if (fromYear === toYear) {
    if (from.getMonth() === to.getMonth()) {
      return `${from.getDate()} – ${to.getDate()} ${toMonth} ${toYear} г.`;
    }
    return `${from.getDate()} ${fromMonth} – ${to.getDate()} ${toMonth} ${toYear} г.`;
  }

  return `${from.getDate()} ${fromMonth} ${fromYear} – ${to.getDate()} ${toMonth} ${toYear}`;
}

const LAST_N_DAYS_PRESETS: Record<Exclude<DatePresetId, 'all' | 'lastWeek'>, { days: number; label: string }> = {
  last7: { days: 7, label: 'Последние 7 дней' },
  last30: { days: 30, label: 'Последние 30 дней' },
  last90: { days: 90, label: 'Последние 90 дней' },
  last365: { days: 365, label: 'Последние 365 дней' },
};

function resolveLastNDays(today: Date, days: number, label: string): ResolvedDateRange {
  const yesterday = subDays(today, 1);
  const from = subDays(today, days);
  const to = endOfDay(yesterday);
  const prevFrom = subDays(from, days);
  const prevTo = endOfDay(subDays(from, 1));
  return { from, to, prevFrom, prevTo, label, dateLabel: formatDateRangeLabel(from, yesterday) };
}

export function resolveDateRange(option: DateRangeOption): ResolvedDateRange {
  const today = startOfDay(new Date());

  if (option.type === 'preset') {
    if (option.id === 'all') {
      const from = new Date(2020, 0, 1);
      const to = endOfDay(today);
      return {
        from,
        to,
        prevFrom: null,
        prevTo: null,
        label: 'За все время',
        dateLabel: 'За все время',
      };
    }

    if (option.id === 'lastWeek') {
      // Неделя — с понедельника: так считают еженедельную отчётность.
      const from = subDays(startOfWeek(today, { weekStartsOn: 1 }), 7);
      const sunday = subDays(from, -6);
      return {
        from,
        to: endOfDay(sunday),
        prevFrom: subDays(from, 7),
        prevTo: endOfDay(subDays(from, 1)),
        label: 'Прошлая неделя',
        dateLabel: formatDateRangeLabel(from, sunday),
      };
    }

    const preset = LAST_N_DAYS_PRESETS[option.id];
    return resolveLastNDays(today, preset.days, preset.label);
  }

  if (option.type === 'month') {
    const from = startOfMonth(new Date(option.year, option.month - 1));
    const to = endOfMonth(new Date(option.year, option.month - 1));
    const prevMonthDate = new Date(option.year, option.month - 2);
    const prevFrom = startOfMonth(prevMonthDate);
    const prevTo = endOfMonth(prevMonthDate);
    const label = `${MONTHS_RU_FULL[option.month - 1]} ${option.year}`;
    return { from, to, prevFrom, prevTo, label, dateLabel: label };
  }

  // custom
  const from = startOfDay(option.from);
  const to = endOfDay(option.to);
  const diffMs = to.getTime() - from.getTime();
  const prevFrom = new Date(from.getTime() - diffMs);
  const prevTo = new Date(from.getTime());
  return {
    from,
    to,
    prevFrom,
    prevTo,
    label: 'Другой диапазон',
    dateLabel: formatDateRangeLabel(from, option.to),
  };
}

/** Генерирует список последних N месяцев */
export function getRecentMonths(
  count: number
): Array<{ year: number; month: number; label: string }> {
  const result: Array<{ year: number; month: number; label: string }> = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Добавляем текущий месяц (например: "Май (текущий)")
  result.push({
    year: currentYear,
    month: now.getMonth() + 1,
    label: `${MONTHS_RU_FULL[now.getMonth()]} (текущий)`
  });

  // Добавляем прошлые месяцы
  for (let i = 1; i <= count; i++) {
    const d = subMonths(now, i);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const label =
      year === currentYear ? MONTHS_RU_FULL[month - 1] : `${MONTHS_RU_FULL[month - 1]} ${year}`;
    result.push({ year, month, label });
  }
  return result;
}

/**
 * Границы периода днями (полночь начала и полночь последнего дня) — в той
 * форме, в какой их отдавал календарь отчётов. Сборщики статистики сами
 * добавляют «до конца дня», и конец суток в `to` сдвинул бы их на день.
 */
export function periodDays(option: DateRangeOption): { from: Date; to: Date } {
  const { from, to } = resolveDateRange(option);
  return { from: startOfDay(from), to: startOfDay(to) };
}
